/* ──────────────────────────────────────────────────────────────────────────
 * InQAi intelligence engines over the geospatial layer (brief §4).
 *
 * The important property of this file: every output is DERIVED from state
 * that is visible on the map, and every recommendation carries the evidence
 * it was derived from. Nothing here invents a finding.
 *
 * That distinction matters more than it sounds. A recommendation panel that
 * emits plausible sentences with no traceable basis is worse than no panel —
 * it manufactures false confidence in a room where people act on what the
 * screen says. So each recommendation states: what triggered it, which
 * objects it read, and how confident the rule is.
 *
 * The confidence values are rule confidence (how reliable the *rule* is),
 * not model confidence — there is no model here. Where the underlying data
 * is SIMULATED, the recommendation inherits that and says so, because a
 * sound rule over invented data still yields an invented conclusion.
 * ────────────────────────────────────────────────────────────────────────── */

import { TRUST } from "../data/provenance";
import { SECTORS, ROUTES, ENTRY_GATES, STATUS_THRESHOLDS } from "../data/kumbhGeo";
import { riskHorizon, ASSUMPTIONS } from "./riskEngine";
import { theatreTotals, layerCounts } from "./metrics";
import { INCIDENTS, SEVERITY, activeIncidents } from "../data/operational";

/* Any recommendation built on simulated inputs is itself simulated. */
function inheritedTrust(sources) {
  return sources.some((s) => s?.provenance?.trust === TRUST.SIMULATED)
    ? TRUST.SIMULATED
    : TRUST.UNVERIFIED;
}

/* ── 1. DECISION INTELLIGENCE ──────────────────────────────────────────
 * Rule-based, transparent, and each rule names its threshold so an operator
 * can argue with it. Consequential actions are flagged requiresApproval —
 * §4 requires a human to remain in the loop for government actions. */
export function decisionIntelligence({ zoneStatus, incidents = INCIDENTS, horizon }) {
  const out = [];
  /* The risk engine is the input that makes these recommendations
     anticipatory rather than reactive. Computed here if the caller did not
     supply one, so every call site gets lead-time-aware output. */
  const h = horizon || riskHorizon({ incidents });

  /* ── Lead-time recommendations ──────────────────────────────────────
     The most operationally valuable item on the list: a zone that is not
     yet critical but will be, with the time remaining. Ranked above
     already-critical zones deliberately — a zone at 92% is a situation
     being managed, whereas one 20 minutes out is a decision window that
     closes. */
  /* Every sector on a path to critical is listed — no arbitrary horizon
     cutoff. An earlier version dropped anything beyond 3 hours, which
     silently hid a zone that was genuinely filling just because it was
     filling slowly; a six-hour warning is the most actionable kind there
     is, because the options are still cheap. Urgency is expressed through
     priority and ordering instead of by discarding the item. */
  h.zones
    .filter((z) => z.minutesToCritical != null && z.minutesToCritical > 0)
    .forEach((z) => {
      const urgent = z.minutesToCritical <= ASSUMPTIONS.URGENT_LEAD_MINUTES;
      const nearTerm = z.minutesToCritical <= ASSUMPTIONS.URGENT_LEAD_MINUTES * 4;
      out.push({
        id: `DEC-LEAD-${z.sectorId}`,
        kind: "Lead-time action",
        priority: urgent ? "critical" : nearTerm ? "high" : "moderate",
        title: {
          en: `Act on ${z.name.en} — critical in ~${z.minutesToCritical} min`,
          mr: `${z.name.mr} — अंदाजे ${z.minutesToCritical} मिनिटांत गंभीर`,
        },
        rationale: {
          en: `Filling at ${z.netPerMin}/min with ${(z.occupancyPct)}% occupied. ` +
              (urgent
                ? `The decision window closes in ~${z.minutesToCritical} minutes.`
                : `Not urgent yet — acting early keeps the cheaper options open.`),
          mr: `${z.netPerMin}/मिनिट दराने भरत आहे, ${z.occupancyPct}% भरले. ` +
              `निर्णयासाठी अंदाजे ${z.minutesToCritical} मिनिटे शिल्लक.`,
        },
        evidence: [
          `Zone ${z.sectorId} at ${z.occupancyPct}%`,
          `Net inflow ${z.netPerMin}/min`,
          ...z.drivers.map((d) => d.text),
        ],
        confidence: 0.68,
        trust: z.trust,
        requiresApproval: urgent,
        focus: z.centre,
        leadMinutes: z.minutesToCritical,
      });
    });

  /* ── Bottleneck recommendations ─────────────────────────────────────
     A chokepoint is actionable in a way an occupancy figure is not: the
     remedy is specific and local — open a gate, widen a lane, stage
     arrivals. So the recommendation names the arithmetic. */
  h.bottlenecks.forEach((b) => {
    const needed = Math.ceil(b.inPerMin / ASSUMPTIONS.GATE_THROUGHPUT_PER_MIN);
    const extra = Math.max(0, needed - b.entryGates);
    out.push({
      id: `DEC-GATE-${b.sectorId}`,
      kind: "Gate capacity",
      priority: b.demandRatio >= 1.25 ? "critical" : "high",
      title: {
        en: extra > 0
          ? `Open ${extra} more entry lane(s) at ${b.name.en}`
          : `Relieve gate pressure at ${b.name.en}`,
        mr: extra > 0
          ? `${b.name.mr} येथे आणखी ${extra} प्रवेश मार्ग उघडा`
          : `${b.name.mr} येथील द्वारावरील ताण कमी करा`,
      },
      rationale: {
        en: `${b.inPerMin}/min arriving against ${b.entryCapacityPerMin}/min of gate capacity ` +
            `(${b.entryGates} gate(s) × ${ASSUMPTIONS.GATE_THROUGHPUT_PER_MIN}/min) — ${b.demandRatio}× over. ` +
            `Arrivals that cannot pass do not disperse; they stack at the gate.`,
        mr: `${b.inPerMin}/मिनिट येत आहेत, क्षमता ${b.entryCapacityPerMin}/मिनिट — ${b.demandRatio}× जास्त.`,
      },
      evidence: [
        `Inbound ${b.inPerMin}/min`,
        `${b.entryGates} entry gate(s) mapped`,
        `Assumed throughput ${ASSUMPTIONS.GATE_THROUGHPUT_PER_MIN}/min/gate`,
      ],
      confidence: 0.6,
      trust: TRUST.SIMULATED,
      requiresApproval: true,
      focus: b.centre,
    });
  });

  /* ── Counter-flow ───────────────────────────────────────────────────
     Worth surfacing because it is a precursor rather than a symptom: people
     pushing against the stream is how a corridor becomes a crush. */
  h.abnormal.forEach((a) => {
    out.push({
      id: `DEC-FLOW-${a.id}`,
      kind: "Counter-flow",
      priority: "high",
      title: {
        en: `Investigate counter-flow in ${a.sectorId}`,
        mr: `${a.sectorId} मध्ये प्रतिप्रवाहाची तपासणी करा`,
      },
      rationale: {
        en: `Movement runs ${a.deviationDeg}° against the dominant direction. ` +
            `Counter-flow in a dense corridor precedes crush conditions.`,
        mr: `हालचाल मुख्य दिशेच्या ${a.deviationDeg}° विरुद्ध आहे.`,
      },
      evidence: [`Flow ${a.id} bearing ${a.bearing}°`, `Deviation ${a.deviationDeg}°`],
      confidence: 0.45,
      trust: TRUST.SIMULATED,
      requiresApproval: false,
      focus: a.coord,
    });
  });


  zoneStatus.forEach((z) => {
    if (z.ratio >= STATUS_THRESHOLDS.red) {
      out.push({
        id: `DEC-DIVERT-${z.id}`,
        kind: "Crowd diversion",
        priority: "critical",
        title: {
          en: `Divert inbound flow from ${z.name.en}`,
          mr: `${z.name.mr} कडील येणारा प्रवाह वळवा`,
        },
        rationale: {
          en: `Occupancy ${Math.round(z.ratio * 100)}% is at or above the ${Math.round(
            STATUS_THRESHOLDS.red * 100
          )}% critical threshold.`,
          mr: `${Math.round(z.ratio * 100)}% भरणा गंभीर मर्यादेवर आहे.`,
        },
        evidence: [`Zone ${z.id} occupancy ${z.current.toLocaleString("en-IN")}/${z.capacity.toLocaleString("en-IN")}`],
        confidence: 0.82,
        trust: inheritedTrust([z]),
        requiresApproval: true,
        focus: z.centre,
      });
    } else if (z.ratio >= STATUS_THRESHOLDS.amber) {
      out.push({
        id: `DEC-DEPLOY-${z.id}`,
        kind: "Additional deployment",
        priority: "high",
        title: {
          en: `Pre-position additional marshals at ${z.name.en}`,
          mr: `${z.name.mr} येथे अतिरिक्त मार्शल तैनात करा`,
        },
        rationale: {
          en: `Occupancy ${Math.round(z.ratio * 100)}% has crossed the ${Math.round(
            STATUS_THRESHOLDS.amber * 100
          )}% elevated threshold but is below critical.`,
          mr: `भरणा ${Math.round(z.ratio * 100)}% — वाढीव मर्यादेपलीकडे, गंभीरपेक्षा कमी.`,
        },
        evidence: [`Zone ${z.id} occupancy ${Math.round(z.ratio * 100)}%`],
        confidence: 0.74,
        trust: inheritedTrust([z]),
        requiresApproval: false,
        focus: z.centre,
      });
    }
  });

  // Prioritise emergency response by severity and age.
  const critical = activeIncidents().filter((i) => SEVERITY[i.severity].rank >= 2);
  critical.forEach((i) => {
    out.push({
      id: `DEC-RESP-${i.id}`,
      kind: "Response prioritisation",
      priority: i.severity === "critical" ? "critical" : "high",
      title: {
        en: `Prioritise ${i.id} — ${i.location.en}`,
        mr: `${i.id} ला प्राधान्य — ${i.location.mr}`,
      },
      rationale: {
        en: `${SEVERITY[i.severity].label.en} severity, status "${i.status}".`,
        mr: `${SEVERITY[i.severity].label.mr} तीव्रता, स्थिती "${i.status}".`,
      },
      evidence: [`Incident ${i.id}`, `Assigned: ${i.assigned.en}`],
      confidence: 0.9,
      trust: inheritedTrust([i]),
      requiresApproval: false,
      focus: i.coord,
    });
  });

  // Alternate route availability, when a diversion exists for a congested zone.
  const trafficIncident = activeIncidents().find((i) => i.type === "traffic");
  const diversion = ROUTES.find((r) => r.layer === "diversionRoutes");
  if (trafficIncident && diversion) {
    out.push({
      id: "DEC-ALT-ROUTE",
      kind: "Alternate route",
      priority: "moderate",
      title: {
        en: `Open ${diversion.name.en}`,
        mr: `${diversion.name.mr} खुला करा`,
      },
      rationale: {
        en: `Active traffic incident ${trafficIncident.id} with a mapped diversion available.`,
        mr: `सक्रिय वाहतूक घटना ${trafficIncident.id}, वळण मार्ग उपलब्ध.`,
      },
      evidence: [`Incident ${trafficIncident.id}`, `Route ${diversion.id}`],
      confidence: 0.61,
      trust: inheritedTrust([trafficIncident, diversion]),
      requiresApproval: true,
      focus: diversion.coords[0],
    });
  }

  /* Sort by priority, then by how little time is left. Two critical items
     are not equally urgent if one has 12 minutes and the other 40. */
  const order = { critical: 0, high: 1, moderate: 2, low: 3 };
  return out.sort((a, b) => {
    const p = order[a.priority] - order[b.priority];
    if (p !== 0) return p;
    const al = a.leadMinutes ?? Infinity;
    const bl = b.leadMinutes ?? Infinity;
    return al - bl;
  });
}

/* ── 2. OPERATIONAL INTELLIGENCE ───────────────────────────────────────
 * Current-state KPIs. Counts only — nothing inferred. */
export function operationalIntelligence({ zoneStatus }) {
  const active = activeIncidents();
  const red = zoneStatus.filter((z) => z.status === "red");
  const amber = zoneStatus.filter((z) => z.status === "amber");
  const totalCurrent = zoneStatus.reduce((s, z) => s + z.current, 0);
  const totalCapacity = zoneStatus.reduce((s, z) => s + z.capacity, 0);

  const totals = theatreTotals({ incidents: INCIDENTS });
  const counts = layerCounts();

  return {
    /* Counts of mapped assets, so "resource deployment" and "emergency
       readiness" in §4 are answered with numbers rather than adjectives. */
    pilgrims: totals.pilgrims,
    capacity: totals.capacity,
    headroom: totals.headroom,
    mappedFeatures: totals.mappedFeatures,
    hospitals: counts.hospitals || 0,
    medicalCamps: counts.medicalCamps || 0,
    ambulancePoints: counts.ambulancePoints || 0,
    policePosts: counts.policePosts || 0,
    commandCentres: counts.commandCentres || 0,
    entryGates: ENTRY_GATES.length,
    zonesTotal: zoneStatus.length,
    zonesRed: red.length,
    zonesAmber: amber.length,
    zonesGreen: zoneStatus.length - red.length - amber.length,
    incidentsActive: active.length,
    incidentsCritical: active.filter((i) => i.severity === "critical").length,
    loadPct: Math.round((totalCurrent / totalCapacity) * 100),
    routesMapped: ROUTES.length,
    evacuationRoutes: ROUTES.filter((r) => r.layer === "evacuationRoutes").length,
    trust: TRUST.SIMULATED,
  };
}

/* ── 3. VISION INTELLIGENCE ────────────────────────────────────────────
 * Architecture only. No CCTV is connected to this POC, and §4 is explicit
 * that we must not claim otherwise. This returns the interface a real
 * analytics pipeline would populate, plus an honest status. */
export function visionIntelligence() {
  return {
    connected: false,
    statusNote:
      "No CCTV or video analytics feed is connected. This panel describes the " +
      "integration contract only — it is not reporting observed events.",
    /* The event shape a detector would emit: detection → geospatial marker. */
    contract: {
      eventId: "string",
      detector: "string (e.g. crowd-density-v2)",
      camera: "string",
      coord: "[lng, lat]",
      detectedAt: "ISO timestamp",
      confidence: "0..1",
      kind: "abnormalFormation | blockedRoute | excessiveDensity | unusualMovement",
      frameRef: "evidence URI (access-controlled)",
    },
    supportedKinds: [
      { key: "abnormalFormation", en: "Abnormal crowd formation", mr: "असामान्य गर्दी रचना" },
      { key: "blockedRoute",      en: "Blocked route",            mr: "अवरोधित मार्ग" },
      { key: "excessiveDensity",  en: "Excessive density",        mr: "अति घनता" },
      { key: "unusualMovement",   en: "Unusual movement",         mr: "असामान्य हालचाल" },
    ],
    events: [], // stays empty until a real feed exists
  };
}

/* ── 4. AUTOMATION INTELLIGENCE ────────────────────────────────────────
 * Policy rules, evaluated against current state. Rules that would cause a
 * consequential action are gated on human approval and report as "would
 * trigger", never as "has actioned". */
export const AUTOMATION_POLICIES = [
  {
    id: "POL-DENSITY",
    when: { en: "Crowd density exceeds approved threshold", mr: "गर्दी घनता मंजूर मर्यादेपेक्षा जास्त" },
    then: { en: "Generate operational alert", mr: "कार्यान्वयन सूचना तयार करा" },
    requiresApproval: false,
  },
  {
    id: "POL-ROUTE",
    when: { en: "Route becomes unavailable", mr: "मार्ग अनुपलब्ध होतो" },
    then: { en: "Identify alternate route", mr: "पर्यायी मार्ग निश्चित करा" },
    requiresApproval: true,
  },
  {
    id: "POL-ESCALATE",
    when: { en: "Critical incident occurs", mr: "गंभीर घटना घडते" },
    then: { en: "Trigger configured escalation workflow", mr: "निश्चित उन्नयन प्रक्रिया सुरू करा" },
    requiresApproval: true,
  },
];

export function automationIntelligence({ zoneStatus }) {
  const active = activeIncidents();
  const fired = [];

  const redZones = zoneStatus.filter((z) => z.ratio >= STATUS_THRESHOLDS.red);
  if (redZones.length) {
    fired.push({
      policy: "POL-DENSITY",
      status: "would-trigger",
      detail: `${redZones.length} zone(s) at or above critical density: ${redZones.map((z) => z.id).join(", ")}`,
      requiresApproval: false,
    });
  }

  const traffic = active.filter((i) => i.type === "traffic");
  if (traffic.length) {
    fired.push({
      policy: "POL-ROUTE",
      status: "awaiting-approval",
      detail: `${traffic.length} active traffic incident(s); alternate route identified but not applied.`,
      requiresApproval: true,
    });
  }

  const criticals = active.filter((i) => i.severity === "critical");
  if (criticals.length) {
    fired.push({
      policy: "POL-ESCALATE",
      status: "awaiting-approval",
      detail: `${criticals.length} critical incident(s): ${criticals.map((i) => i.id).join(", ")}`,
      requiresApproval: true,
    });
  }

  return { policies: AUTOMATION_POLICIES, fired, trust: TRUST.SIMULATED };
}

/* ── 5. EVALUATION INTELLIGENCE ────────────────────────────────────────
 * Measured from incident timestamps we actually hold. Where we cannot
 * measure something, we return null and the UI omits it rather than
 * printing a fabricated benchmark. */
export function evaluationIntelligence() {
  const resolved = INCIDENTS.filter((i) => i.status === "Resolved");

  const resolutionMins = resolved
    .map((i) => (new Date(i.updatedAt) - new Date(i.reportedAt)) / 60000)
    .filter((n) => Number.isFinite(n) && n >= 0);

  const avgResolution = resolutionMins.length
    ? Math.round(resolutionMins.reduce((a, b) => a + b, 0) / resolutionMins.length)
    : null;

  // First-response time is not captured in the data model, so we do not
  // estimate it. An "acknowledgedAt" field would make this measurable.
  const avgFirstResponse = null;

  const bySector = SECTORS.map((s) => ({
    id: s.id,
    name: s.name,
    incidents: INCIDENTS.filter((i) => i.sector === s.id).length,
    open: activeIncidents().filter((i) => i.sector === s.id).length,
  }));

  return {
    avgResolutionMins: avgResolution,
    avgFirstResponseMins: avgFirstResponse,
    firstResponseNote:
      "Not measurable — the incident model has no acknowledgement timestamp. " +
      "Add `acknowledgedAt` to capture it.",
    resolvedCount: resolved.length,
    totalCount: INCIDENTS.length,
    bySector,
    historicalComparison: null,
    historicalNote:
      "No historical baseline exists in this POC. Comparison requires a " +
      "persisted incident history, which needs a backend.",
    trust: TRUST.SIMULATED,
  };
}
